import { defineStore } from 'pinia'
import { bffApi } from '@shared/service/bff'
import { authService } from '@/app/Services'
import { requestBffApi } from '@/app/services/bffRequest'

type AccessStore = {
  accountAccess:
    | undefined
    | {
        address: string
        hasClaimedCode: boolean
      }
}

const fetchAccessStatus = async (address: string) => {
  try {
    const { data } = await requestBffApi({
      request: () =>
        bffApi.api.v1.entry_code.verify.get({
          params: {
            query: { address }
          }
        })
    })

    return !!data?.data?.hasClaimedEntryCode
  } catch {
    return false
  }
}

const initialStateFactory = (): AccessStore => ({
  accountAccess: undefined
})

export const useAccessStore = defineStore('access', {
  state: (): AccessStore => initialStateFactory(),

  getters: {
    userHasAccess: (state): boolean => {
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return false
      }

      if (state.accountAccess?.address !== sharedWalletStore.injectiveAddress) {
        return false
      }

      return !!state.accountAccess?.hasClaimedCode
    }
  },

  actions: {
    setAccountAccess(accountAccess: AccessStore['accountAccess']) {
      this.accountAccess = accountAccess
    },

    async claimCode(code: string) {
      const accessStore = useAccessStore()
      const sharedWalletStore = useSharedWalletStore()
      const injectiveAddress = sharedWalletStore.injectiveAddress

      const result = await authService.verifyEntryCode(code)

      if (!result.isValid) {
        throw new Error('404')
      }

      accessStore.setAccountAccess({
        address: injectiveAddress,
        hasClaimedCode: true
      })
    },

    async forceFetchAccessStatus() {
      const accessStore = useAccessStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const hasClaimedCode = await fetchAccessStatus(
        sharedWalletStore.injectiveAddress
      )

      accessStore.setAccountAccess({
        hasClaimedCode,
        address: sharedWalletStore.injectiveAddress
      })
    },

    async fetchAccessStatus() {
      const accessStore = useAccessStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      if (sharedWalletStore.isReadOnly) {
        accessStore.setAccountAccess({
          hasClaimedCode: true,
          address: sharedWalletStore.injectiveAddress
        })

        return
      }

      if (
        accessStore.userHasAccess &&
        accessStore.accountAccess?.address ===
          sharedWalletStore.injectiveAddress
      ) {
        return
      }

      const hasClaimedCode = await fetchAccessStatus(
        sharedWalletStore.injectiveAddress
      )

      accessStore.setAccountAccess({
        hasClaimedCode,
        address: sharedWalletStore.injectiveAddress
      })
    }
  }
})
